import type {Table} from '@wandb/weave/common/util/csv';

import {
  captureUniqueError,
  queryToTable,
  sampleQueryHistorySpecs,
} from '../components/Export/utils';
import type {BarChartConfig} from '../components/PanelBarChart/types';
import type {RunsLinePlotConfig} from '../components/PanelRunsLinePlot/types';
import type {RunsDataQueryConfig} from '../containers/RunsDataLoader';
import {useRunsData} from '../state/runs/hooks';
import type {Query} from '../util/queryTypes';
import type {PanelConfig} from './panelTypes';
import {isAnyRunsetGrouped} from './runhelpers';

export function isQueryConfigGrouped(
  config: RunsLinePlotConfig | BarChartConfig
) {
  return config.aggregate || config.aggregateMetrics;
}

/**
 * @deprecated use `isQueryConfigGrouped` and `isAnyRunsetGrouped` instead
 * The problem with this function is that it determines grouping by looking at the query config, and also checking to see if any contained subquery (i.e. runsetquery) is grouped. This might be correct, but can lead to errors, e.g.
 * 1. I have a query config with no grouping and two runsets, and only one runset is grouped
 * 2. I call `isGrouped` on the query config, and it return true, indicating grouping is active somewhere inside it
 * 3. I treat that `isGrouped = true` value downstream, where I might be operating on the ungrouped runset query. This is an error.
 */
export function isGrouped(
  query: Query,
  config: RunsLinePlotConfig | BarChartConfig
) {
  // Runs can be grouped in two ways:
  // 1) If there is grouping in the data populating the plot, the plot
  // will have grouping.
  // 2) the user selected grouping in the panel (config.aggregate)
  if (config.useRunsTableGroupingInPanels ?? true) {
    return (
      isAnyRunsetGrouped(query.runSets ?? []) ||
      config.aggregate ||
      config.aggregateMetrics
    );
  }

  return config.aggregate || config.aggregateMetrics;
}

export function useSampleAndQueryToTable(
  query: RunsDataQueryConfig,
  pageQuery: Query,
  config: PanelConfig
) {
  query = sampleQueryHistorySpecs(query);
  const runsData = useRunsData(query);

  let table: Table = {cols: [], data: []};
  try {
    table = queryToTable(runsData.data, query, pageQuery, config);
  } catch (e) {
    captureUniqueError(e);
  }

  return {table, loading: runsData.loading};
}

export function getConfigWithDefaults(configSpec: any, config: any) {
  const spec = configSpec || {};
  const result = {} as any;
  for (const key of Object.keys(spec)) {
    result[key] = config[key] ?? spec[key].default;
  }
  return result;
}
